import { useDispatch, useSelector } from 'react-redux';
import { getProfile, selectTreatmentPlans } from '../../../../../redux/modules/profile/selectors';
import { TreatmentPlan } from '../../../../../packages/ui/templates/sign-off/components/modalities';
import { useEffect, useState } from 'react';
import { updatePatientTreatmentPlan } from '../../../../../services/profile/profile.service';
import { useParams } from 'react-router-dom';
import { fontWeight, Heading, headingLevel } from '../../../../../packages';
import { Box } from '@mui/material';
import { EditTreatmentPlanDrawer } from '../../../sessions/sign-off-v4/sections/EditTreatmentPlanDrawer';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { ProblemDrawer } from '../../../sessions/sign-off-v4/sections/ProblemDrawer';
import { GoalDrawer } from '../../../sessions/sign-off-v4/sections/GoalDrawer';
import EmptyState from '../EmptyState';
import { showSnackbar } from '../../../../../redux/modules/snackbar';

const { useStyles } = require('./index.styles');

export const AllTreatmentPlans = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const treatmentPlans = useSelector(selectTreatmentPlans);
  const { demographicDetails, selectedPatient, profile } = useSelector(getProfile);
  const { memberId } = useParams();
  const [currentTreatmentPlan, setCurrentTreatmentPlan] = useState(null);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);

  const updatePlan = async plan => {
    setCurrentTreatmentPlan(null);
    setOpenEditDrawer(false);
    try {
      const response = await updatePatientTreatmentPlan(memberId, plan);
      if (response.data) {
        dispatch(profileActionCreators.fetchTreatmentPlans({ memberId }));
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Treatment plan updated successfully.',
          }),
        );
      }
    } catch (error) {
      console.error('Error saving treatment plan:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save treatment plan. Please try again.',
        }),
      );
    }
  };

  const handleAddGoal = problem => {
    setSelectedGoal({
      id: null,
      name: '',
      details: '',
      objectives: [],
      new: true,
      problemId: problem.id,
    });
  };

  const handleAddProblem = () => {
    setSelectedProblem({
      id: null,
      detail: '',
      goals: [],
      new: true,
    });
  };

  const handleProblemSubmit = async updatedProblem => {
    try {
      // Extract recommendationIds before processing
      const updatedPlan = {
        ...currentTreatmentPlan,
        problems: currentTreatmentPlan.problems.some(p => p.id === updatedProblem.id)
          ? currentTreatmentPlan.problems.map(p => (p.id === updatedProblem.id ? updatedProblem : p))
          : [...currentTreatmentPlan.problems, updatedProblem],
      };
      setCurrentTreatmentPlan(null);
      setSelectedProblem(null);
      await updatePlan(updatedPlan);
    } catch (error) {
      console.error('Error submitting problem:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save problem. Please try again.',
        }),
      );
    }
  };

  const handleGoalSubmit = async updatedGoal => {
    try {
      // Make a deep copy to avoid reference issues
      const processedGoal = JSON.parse(JSON.stringify(updatedGoal));

      // Store the problem ID if it was passed in the original goal
      const problemId = processedGoal.problemId;
      delete processedGoal.problemId; // Remove it as it's not part of the data model

      // Create updated plan
      let updatedPlan;

      // If we know which problem this goal belongs to
      if (problemId) {
        updatedPlan = {
          ...currentTreatmentPlan,
          problems: currentTreatmentPlan.problems.map(problem => {
            if (problem.id === problemId) {
              // Return updated problem with goal replaced or added
              const existingGoalIndex = problem.goals.findIndex(g => g.id === processedGoal.id);

              if (existingGoalIndex >= 0) {
                // Replace existing goal
                const updatedGoals = [...problem.goals];
                updatedGoals[existingGoalIndex] = processedGoal;

                return {
                  ...problem,
                  goals: updatedGoals,
                };
              } else {
                // Add as new goal
                return {
                  ...problem,
                  goals: [...problem.goals, processedGoal],
                };
              }
            }
            return problem;
          }),
        };
      } else {
        // Try to find the goal in any problem
        let foundInProblem = false;

        updatedPlan = {
          ...currentTreatmentPlan,
          problems: currentTreatmentPlan.problems.map(problem => {
            // Check if this goal exists in the current problem
            const goalIndex = problem.goals.findIndex(g => g.id === processedGoal.id);

            if (goalIndex >= 0) {
              foundInProblem = true;

              // Replace the goal at the same index
              const updatedGoals = [...problem.goals];
              updatedGoals[goalIndex] = processedGoal;

              return {
                ...problem,
                goals: updatedGoals,
              };
            }

            // Not in this problem, return unchanged
            return problem;
          }),
        };

        // If goal wasn't found in any problem, it must be new
        // Add it to the first problem as a fallback
        if (!foundInProblem && processedGoal.new && updatedPlan.problems.length > 0) {
          updatedPlan.problems[0].goals.push(processedGoal);
        }
      }

      // Update state and save
      setCurrentTreatmentPlan(null);
      setSelectedGoal(null);
      await updatePlan(updatedPlan);
    } catch (error) {
      console.error('Error saving goal:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to save goal. Please try again.',
        }),
      );
    }
  };

  const handleGoalDelete = async (plan, goalId) => {
    try {
      const updatedPlan = {
        ...plan,
        problems: plan.problems.map(p => ({
          ...p,
          goals: p.goals.filter(g => g.id !== goalId),
        })),
      };
      setCurrentTreatmentPlan(null);
      setSelectedGoal(null);
      await updatePlan(updatedPlan);
    } catch (error) {
      console.error('Error deleting goal:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to delete goal. Please try again.',
        }),
      );
    }
  };

  const handleProblemDelete = async (plan, problemId) => {
    try {
      const updatedPlan = {
        ...plan,
        problems: plan.problems.filter(p => p.id !== problemId),
      };
      setCurrentTreatmentPlan(null);
      setSelectedProblem(null);
      await updatePlan(updatedPlan);
    } catch (error) {
      console.error('Error deleting problem:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to delete problem. Please try again.',
        }),
      );
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
          Treatment Plans
        </Heading>
      </Box>
      {treatmentPlans.length > 0 ? (
        treatmentPlans.map((treatmentPlan, index) => {
          return (
            <TreatmentPlan
              key={index}
              onEditProblem={problem => {
                setCurrentTreatmentPlan(treatmentPlan);
                setSelectedProblem(problem);
              }}
              onEditGoal={goal => {
                setCurrentTreatmentPlan(treatmentPlan);
                setSelectedGoal(goal);
              }}
              onEditTreatmentPlan={() => {
                setCurrentTreatmentPlan(treatmentPlan);
                setOpenEditDrawer(true);
              }}
              patient={selectedPatient}
              hideModalities
              editRestricted={profile.designation !== treatmentPlan.providerRole}
              onAddGoal={problem => {
                setCurrentTreatmentPlan(treatmentPlan);
                handleAddGoal(problem);
              }}
              onAddProblem={() => {
                setCurrentTreatmentPlan(treatmentPlan);
                handleAddProblem();
              }}
              onDeleteGoal={goalId => {
                return handleGoalDelete(treatmentPlan, goalId);
              }}
              onDeleteProblem={problemId => {
                return handleProblemDelete(treatmentPlan, problemId);
              }}
              treatmentPlan={treatmentPlan}
            />
          );
        })
      ) : (
        <EmptyState
          title="No treatment plan yet"
          subtitle={`${demographicDetails.nickName} doesn't have a treatment plan yet.`}
          glyph="lottie-treatment-plan-empty"
          glyphOrientation="portrait"
          isEmptyWithLottie
          lottieSize={128}
        />
      )}
      {selectedProblem && (
        <ProblemDrawer
          isOpen={!!selectedProblem}
          onClose={() => setSelectedProblem(null)}
          problem={selectedProblem}
          onSubmit={handleProblemSubmit}
        />
      )}
      {selectedGoal && (
        <GoalDrawer
          isOpen={!!selectedGoal}
          onClose={() => setSelectedGoal(null)}
          goal={selectedGoal}
          onSubmit={handleGoalSubmit}
        />
      )}

      {openEditDrawer && (
        <EditTreatmentPlanDrawer
          isOpen={!!openEditDrawer}
          onClose={() => setOpenEditDrawer(false)}
          treatmentPlan={currentTreatmentPlan}
          onSubmit={updatePlan}
          practitionerDesignation={profile.designation}
        />
      )}
    </>
  );
};
